import React, { useState } from 'react';
import { useProject } from '../context/ProjectContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { 
  Plus, 
  Building2, 
  MapPin, 
  Phone, 
  Mail, 
  MoreVertical, 
  Pencil, 
  Trash2,
  FolderOpen,
  Search,
  ChevronRight,
  Home
} from 'lucide-react';

const KundenView = ({ onSelectCustomer, onSelectBuilding }) => {
  const { 
    customers, 
    addCustomer, 
    updateCustomer, 
    deleteCustomer: removeCustomer,
    addBuilding,
    deleteBuilding: removeBuilding,
    getProjectsByBuilding 
  } = useProject();

  const [isNewCustomerOpen, setIsNewCustomerOpen] = useState(false);
  const [isNewBuildingOpen, setIsNewBuildingOpen] = useState(false);
  const [selectedCustomerId, setSelectedCustomerId] = useState(null);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedCustomers, setExpandedCustomers] = useState({});

  const [newCustomer, setNewCustomer] = useState({
    name: '', ansprechpartner: '', strasse: '', plz: '', ort: '', telefon: '', email: ''
  });

  const [newBuilding, setNewBuilding] = useState({
    name: '', address: ''
  });

  const handleCreateCustomer = () => {
    if (newCustomer.name.trim()) {
      addCustomer(newCustomer);
      setNewCustomer({ name: '', ansprechpartner: '', strasse: '', plz: '', ort: '', telefon: '', email: '' });
      setIsNewCustomerOpen(false);
    }
  };

  const handleCreateBuilding = () => {
    if (newBuilding.name.trim() && selectedCustomerId) {
      addBuilding(selectedCustomerId, newBuilding);
      setNewBuilding({ name: '', address: '' });
      setIsNewBuildingOpen(false);
      setSelectedCustomerId(null);
    }
  };

  const handleDeleteCustomer = (id) => {
    if (window.confirm('Möchten Sie diesen Kunden wirklich löschen?')) {
      removeCustomer(id);
    }
  };

  const handleDeleteBuilding = (customerId, buildingId) => {
    if (window.confirm('Möchten Sie dieses Gebäude wirklich löschen?')) {
      removeBuilding(customerId, buildingId);
    }
  };

  const toggleExpanded = (customerId) => {
    setExpandedCustomers(prev => ({
      ...prev,
      [customerId]: !prev[customerId]
    }));
  };

  const filteredCustomers = customers.filter(c =>
    c.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.ort.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="h-full flex flex-col bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-semibold text-gray-800">Kundenverwaltung</h1>
            <p className="text-sm text-gray-500 mt-1">{customers.length} Kunden</p>
          </div>
          <Button onClick={() => setIsNewCustomerOpen(true)} className="bg-emerald-600 hover:bg-emerald-700">
            <Plus className="w-4 h-4 mr-2" />
            Neuer Kunde
          </Button>
        </div>

        {/* Search */}
        <div className="mt-4 relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input
            placeholder="Kunden suchen..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 w-full max-w-md"
          />
        </div>
      </div>

      {/* Customer List */}
      <div className="flex-1 overflow-auto p-6">
        <div className="space-y-4">
          {filteredCustomers.map((customer) => (
            <Card key={customer.id} className="overflow-hidden">
              <div 
                className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-50"
                onClick={() => toggleExpanded(customer.id)}
              >
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center">
                    <Building2 className="w-6 h-6 text-emerald-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-800">{customer.name}</h3>
                    <p className="text-sm text-gray-500">{customer.ansprechpartner}</p>
                    <div className="flex items-center gap-4 mt-1 text-xs text-gray-400">
                      <span className="flex items-center gap-1">
                        <MapPin className="w-3 h-3" />
                        {customer.ort}
                      </span>
                      <span className="flex items-center gap-1">
                        <Home className="w-3 h-3" />
                        {customer.buildings.length} Gebäude
                      </span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                      <Button variant="ghost" size="sm">
                        <MoreVertical className="w-4 h-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={(e) => {
                        e.stopPropagation();
                        setEditingCustomer(customer);
                      }}>
                        <Pencil className="w-4 h-4 mr-2" />
                        Bearbeiten
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={(e) => {
                        e.stopPropagation();
                        setSelectedCustomerId(customer.id);
                        setIsNewBuildingOpen(true);
                      }}>
                        <Plus className="w-4 h-4 mr-2" />
                        Gebäude hinzufügen
                      </DropdownMenuItem>
                      <DropdownMenuItem 
                        className="text-red-600"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteCustomer(customer.id);
                        }}
                      >
                        <Trash2 className="w-4 h-4 mr-2" />
                        Löschen
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                  <ChevronRight className={`w-5 h-5 text-gray-400 transition-transform ${expandedCustomers[customer.id] ? 'rotate-90' : ''}`} />
                </div>
              </div>

              {/* Expanded: Buildings */}
              {expandedCustomers[customer.id] && (
                <div className="border-t border-gray-100 bg-gray-50 p-4">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="text-sm font-medium text-gray-600">Gebäude</h4>
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => {
                        setSelectedCustomerId(customer.id);
                        setIsNewBuildingOpen(true);
                      }}
                    >
                      <Plus className="w-3 h-3 mr-1" />
                      Gebäude
                    </Button>
                  </div>
                  {customer.buildings.length === 0 ? (
                    <p className="text-sm text-gray-400 italic">Keine Gebäude angelegt</p>
                  ) : (
                    <div className="space-y-2">
                      {customer.buildings.map((building) => {
                        const projectCount = getProjectsByBuilding(building.id).length;
                        return (
                          <div 
                            key={building.id}
                            className="flex items-center justify-between p-3 bg-white rounded-lg border border-gray-200 hover:border-emerald-300 cursor-pointer"
                            onClick={() => onSelectBuilding && onSelectBuilding(customer, building)}
                          >
                            <div className="flex items-center gap-3">
                              <Home className="w-5 h-5 text-gray-400" />
                              <div>
                                <p className="font-medium text-gray-700">{building.name}</p>
                                <p className="text-xs text-gray-400">{building.address}</p>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
                                {projectCount} Projekte
                              </span>
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                                  <Button variant="ghost" size="sm" className="h-6 w-6 p-0">
                                    <MoreVertical className="w-3 h-3" />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                  <DropdownMenuItem onClick={(e) => {
                                    e.stopPropagation();
                                    // TODO: Edit building
                                  }}>
                                    <Pencil className="w-4 h-4 mr-2" />
                                    Bearbeiten
                                  </DropdownMenuItem>
                                  <DropdownMenuItem 
                                    className="text-red-600"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleDeleteBuilding(customer.id, building.id);
                                    }}
                                  >
                                    <Trash2 className="w-4 h-4 mr-2" />
                                    Löschen
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                              <ChevronRight className="w-4 h-4 text-gray-400" />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </Card>
          ))}
        </div>
      </div>

      {/* New Customer Dialog */}
      <Dialog open={isNewCustomerOpen} onOpenChange={setIsNewCustomerOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Neuen Kunden anlegen</DialogTitle>
            <DialogDescription>
              Geben Sie die Kundendaten ein.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2 space-y-2">
                <Label>Firmenname / Name *</Label>
                <Input
                  value={newCustomer.name}
                  onChange={(e) => setNewCustomer({...newCustomer, name: e.target.value})}
                  placeholder="z.B. Müller GmbH"
                />
              </div>
              <div className="col-span-2 space-y-2">
                <Label>Ansprechpartner</Label>
                <Input
                  value={newCustomer.ansprechpartner}
                  onChange={(e) => setNewCustomer({...newCustomer, ansprechpartner: e.target.value})}
                  placeholder="z.B. Hans Müller"
                />
              </div>
              <div className="col-span-2 space-y-2">
                <Label>Straße</Label>
                <Input
                  value={newCustomer.strasse}
                  onChange={(e) => setNewCustomer({...newCustomer, strasse: e.target.value})}
                  placeholder="z.B. Musterstraße 123"
                />
              </div>
              <div className="space-y-2">
                <Label>PLZ</Label>
                <Input
                  value={newCustomer.plz}
                  onChange={(e) => setNewCustomer({...newCustomer, plz: e.target.value})}
                  placeholder="12345"
                />
              </div>
              <div className="space-y-2">
                <Label>Ort</Label>
                <Input
                  value={newCustomer.ort}
                  onChange={(e) => setNewCustomer({...newCustomer, ort: e.target.value})}
                  placeholder="München"
                />
              </div>
              <div className="space-y-2">
                <Label>Telefon</Label>
                <Input
                  value={newCustomer.telefon}
                  onChange={(e) => setNewCustomer({...newCustomer, telefon: e.target.value})}
                  placeholder="089 12345678"
                />
              </div>
              <div className="space-y-2">
                <Label>E-Mail</Label>
                <Input
                  value={newCustomer.email}
                  onChange={(e) => setNewCustomer({...newCustomer, email: e.target.value})}
                  placeholder="info@kunde.de"
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsNewCustomerOpen(false)}>
              Abbrechen
            </Button>
            <Button onClick={handleCreateCustomer} className="bg-emerald-600 hover:bg-emerald-700">
              Anlegen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* New Building Dialog */}
      <Dialog open={isNewBuildingOpen} onOpenChange={setIsNewBuildingOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Neues Gebäude anlegen</DialogTitle>
            <DialogDescription>
              Geben Sie die Gebäudedaten ein.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Gebäudename *</Label>
              <Input
                value={newBuilding.name}
                onChange={(e) => setNewBuilding({...newBuilding, name: e.target.value})}
                placeholder="z.B. Hauptgebäude, Lagerhalle"
              />
            </div>
            <div className="space-y-2">
              <Label>Adresse</Label>
              <Input
                value={newBuilding.address}
                onChange={(e) => setNewBuilding({...newBuilding, address: e.target.value})}
                placeholder="z.B. Industriestr. 15, 80995 München"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsNewBuildingOpen(false)}>
              Abbrechen
            </Button>
            <Button onClick={handleCreateBuilding} className="bg-emerald-600 hover:bg-emerald-700">
              Anlegen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default KundenView;
